import { useEffect, useState } from 'react';
import { useStateRef } from './useStateRef';
/**
 * A hook that animates an element when it mounts and unmounts.
 * Does not handle DOM insertion/removal. Use the `isMounted` return value to conditionally render the element.
 * @param open Whether the element is open or not
 * @param enter The animation to play when the element mounts
 * @param enterCallback A callback to run after the enter animation finishes
 * @param exit The animation to play when the element unmounts
 * @param exitCallback A callback to run after the exit animation finishes
 * @returns A tuple containing whether the element can be mounted and a ref callback to set the element
 */
export const useLifecycleAnimation = ({ open, enter, enterCallback, exit, exitCallback }, disabled = false) => {
    const [element, setElement] = useState(null);
    const [isMounted, setIsMounted] = useState(() => open);
    useEffect(() => {
        if (disabled || open) {
            setIsMounted(open);
        }
    }, [disabled, open]);
    // Using "state ref"s to prevent changes from re-running the effect below
    // We only want changes to `open` and `element` to re-run the effect
    const enterRef = useStateRef(enter);
    const enterCallbackRef = useStateRef(enterCallback);
    const exitRef = useStateRef(exit);
    const exitCallbackRef = useStateRef(exitCallback);
    useEffect(() => {
        if (!element) {
            return;
        }
        if (disabled) {
            setIsMounted(open);
            return;
        }
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const enter = enterRef.current;
        const enterCallback = enterCallbackRef.current;
        const exit = exitRef.current;
        const exitCallback = exitCallbackRef.current;
        if (prefersReducedMotion && !(enter === null || enter === void 0 ? void 0 : enter.reducedMotionKeyframes) && !(exit === null || exit === void 0 ? void 0 : exit.reducedMotionKeyframes)) {
            setIsMounted(open);
            return;
        }
        let animation;
        if (open) {
            if (!enter) {
                setIsMounted(true);
                enterCallback === null || enterCallback === void 0 ? void 0 : enterCallback(element);
                return;
            }
            if (enter.initialStyle) {
                Object.assign(element.style, enter.initialStyle);
            }
            animation = element.animate(prefersReducedMotion && enter.reducedMotionKeyframes ? enter.reducedMotionKeyframes : enter.keyframes, {
                ...enter.options,
                fill: 'forwards',
            });
            animation.finished
                .then(() => {
                enterCallback === null || enterCallback === void 0 ? void 0 : enterCallback(element);
            })
                .catch((error) => {
                if ((animation === null || animation === void 0 ? void 0 : animation.currentTime) === null) {
                    return;
                }
                console.error(error);
            });
        }
        else {
            if (!exit) {
                setIsMounted(false);
                exitCallback === null || exitCallback === void 0 ? void 0 : exitCallback(element);
                return;
            }
            if (exit.initialStyle) {
                Object.assign(element.style, exit.initialStyle);
            }
            animation = element.animate(prefersReducedMotion && exit.reducedMotionKeyframes ? exit.reducedMotionKeyframes : exit.keyframes, {
                ...exit.options,
                fill: 'forwards',
            });
            animation.finished
                .then(() => {
                setIsMounted(false);
                exitCallback === null || exitCallback === void 0 ? void 0 : exitCallback(element);
            })
                .catch((error) => {
                if ((animation === null || animation === void 0 ? void 0 : animation.currentTime) === null) {
                    return;
                }
                console.error(error);
            });
        }
        return () => {
            animation === null || animation === void 0 ? void 0 : animation.cancel();
        };
    }, [open, element, enterRef, enterCallbackRef, exitRef, exitCallbackRef, disabled]);
    return [isMounted, setElement, element];
};
